import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError } from "../../../lib/http/errors";
import { activityPhase } from "../../../lib/api/fieldApi";
import type { TeacherActivity } from "../../../lib/api/fieldApi";
import { useFieldApi } from "../../../hooks/useFieldApi";
import { isTodayOrUnset, whenLabel } from "../../../lib/activityTime";
import { Button, ButtonLink } from "../../../components/ui/Button";
import { ErrorBanner, ScreenRoot } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import { hasMessage, useI18n, useT } from "../../../i18n";
import type { Translate as T } from "../../../i18n";
import { TeacherRoomCell, useTeacherRoom } from "../../room";
import { useSession } from "../../../store/session/SessionProvider";
import { useExchangeApi } from "../../exchange";
import {
  NETCHECK_PATH,
  assessPath,
  exchangePath,
  playgroundPath,
  roomConsolePath,
  videoPath,
} from "../../../constants/paths";
import "../../../styles/video.css";
import "../../../styles/exchange.css";
import type { OverviewLoad } from "../types";

/**
 * FS-01-04 교사 홈 — 화면 설계 설명서 v1.0 2.1(개선 화면) + 보완 v0.1(docs/구현/참조). 수업 직전에 '오늘 무엇을 · 누가 준비됐나 · 무엇을 누르나'만 위에.
 *  - 오늘 수업 카드: 차시 · 활동 이름 · 모드 · 시간 · 예정 / 상태 칩(활동 상태 · 올린 학생 n/전체 · 공개) / 아직 안 올린 학생 이름 칩.
 *    화면에서 유일한 큰 버튼 '▶ 룸 열기'(그 활동으로 룸 열기 → 룸 콘솔 1 준비). 룸이 열려 있으면 같은 자리가 '룸 콘솔 →'.
 *  - 룸 상태 칸(TeacherRoomCell): 닫힘 · 여는 중 · 열림 · 끊김(보완 2.5 — 빨간 칩 + '↻ 다시 연결') · 열림이면 '룸 콘솔 →' · '칠판 뷰'. 10초마다 새로 확인.
 *    모드 바꾸기 · 룸 닫기는 룸 콘솔에서(설명서 2.1 H).
 *  - 다음 · 지난 활동: 활동마다 한 줄(이름 · 모드 · 시간 · 공개 칩 · 예정 · 상태 칩 · 영상 칩 · 바로 가기 2개).
 *    끝난 활동에 성취를 다 보내지 못했으면 노란 '성취 보내기 남음 n명'(보완 2.3 — 메모 · 성취로). 옛 'AGS 미전송' 칸은 자리표시라 뺐다.
 *  - 설정 · 도구(맨 아래 한 줄): 놀이터 설정 · 리플레이 · 교류 룸 초대(받은 초대 수) · 학교망 진단. 반응 집계 · 작품명은 놀이터 설정 안 탭.
 *  - 점수 · 순위 없음 — 숫자는 '몇 명 올렸나'만.
 * 개요 데이터: GET …/teacher/overview(S5 계약 — missing · works 이름은 서버 별명). 404 면 '활동 없음' 상태, 그 밖 실패는 오류 배너.
 */
export function TeacherHomeScreen() {
  const t = useT();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const state = useSession();
  const api = useFieldApi();
  const courseId = state.status === "ready" ? state.session.courseId : "";
  const [load, setLoad] = useState<OverviewLoad>({ status: "loading" });
  const room = useTeacherRoom();
  const received = useReceivedInvites();

  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    api
      .teacherOverview()
      .then((data) => !cancelled && setLoad({ status: "ready", data }))
      .catch(
        (e: unknown) =>
          !cancelled &&
          setLoad({ status: "error", code: e instanceof ApiError ? e.code : "NET-SERVER" }),
      );
    return () => {
      cancelled = true;
    };
  }, [api]);

  const data = load.status === "ready" ? load.data : null;
  const chips = [];
  if (data?.courseLabel) chips.push({ label: data.courseLabel, testId: "course-chip" });
  if (data?.courseCount != null)
    chips.push({ label: t("teacher.home.courseCount", { n: data.courseCount }) });
  useTopBar({ icon: "👩‍🏫", title: t("teacher.home.appTitle"), chips });

  const pg = playgroundPath(courseId);
  const sub = data?.submissions;
  const activities = data?.activities ?? [];
  const today = pickToday(activities);
  const others = activities.filter((a) => a !== today);
  const roomOpen = room.st.k === "open" ? room.st.roomId : null;
  const openFor = async (a?: TeacherActivity) => {
    const r = await room.open(a?.id);
    // 룸을 열면 1 준비(모드 고르기 → 시작 — 설명서 2.0 흐름 · 보완 v0.1 4장)
    if (r) navigate(`${roomConsolePath(r.roomId)}/teams`);
  };
  const modeOf = (a: TeacherActivity) => {
    const id = a.modeId === "gallery" ? "gallery" : a.modeId;
    const mode =
      id && hasMessage(`teacher.room.modeName.${id}`)
        ? t(`teacher.room.modeName.${id}`)
        : id && hasMessage(`teacher.modes.${id}`)
          ? t(`teacher.modes.${id}`)
          : (a.mode ?? id ?? null);
    const preset =
      a.presetKey && hasMessage(`course.preset.${a.presetKey}`)
        ? t(`course.preset.${a.presetKey}`)
        : (a.preset ?? null);
    return [mode, preset].filter(Boolean).join(" · ");
  };
  const minutesOf = (a: TeacherActivity) => {
    const m = a.timeBudgetS != null ? Math.round(a.timeBudgetS / 60) : (a.minutes ?? null);
    return m != null ? t("teacher.home.minutes", { n: m }) : null;
  };
  const busy = room.st.k === "busy" || room.st.k === "loading";
  const todayPhase = today ? activityPhase(today.status) : null;
  const todayCanOpen = !!today && canOpen(today);
  const missing = sub?.missing ?? [];

  return (
    <ScreenRoot id="FS-01-04" title={t("teacher.home.title")} hideTitle className="pf-teacher">
      {load.status === "error" && (
        <ErrorBanner
          cause={t("teacher.home.loadFailed")}
          action={t("teacher.common.retryLater")}
          code={load.code}
        />
      )}
      <div className="pf-today__wrap">
        <section
          className="pf-pane pf-today"
          aria-labelledby="today-title"
          data-testid="today-card"
        >
          <p className="pf-muted pf-today__kicker">{t("teacher.home.todayKicker")}</p>
          {today ? (
            <>
              <h2 id="today-title" className="pf-h2 pf-today__title">
                {/* 활동 이름에 이미 차시가 있으면 붙이지 않는다('5차시 마무리 놀이터') */}
                {today.lessonNo != null &&
                  !today.title.includes(t("teacher.home.lessonNo", { no: today.lessonNo })) && (
                    <span className="pf-today__lesson">
                      {t("teacher.home.lessonWrap", { no: today.lessonNo })}
                    </span>
                  )}
                {today.title}
              </h2>
              <p className="pf-muted" data-testid="today-meta">
                {[modeOf(today), minutesOf(today), whenLabel(today, t, locale)]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              <div className="pf-row pf-row--wrap pf-today__chips" data-testid="today-chips">
                <StatusChip a={today} t={t} />
                {sub && (
                  <span
                    className="pf-chip-static pf-chip-static--info"
                    data-testid="today-uploaded"
                  >
                    {t("teacher.home.uploadedChip", { n: sub.uploaded, total: sub.total })}
                  </span>
                )}
                <VisibilityChip a={today} t={t} to={`${pg}?tab=settings`} />
                {todayPhase === "done" && <AchLeftChip a={today} t={t} />}
              </div>
            </>
          ) : (
            <h2 id="today-title" className="pf-h3" data-testid="no-activities">
              {load.status === "loading" ? t("common.loading") : t("teacher.home.noActivities")}
            </h2>
          )}
          {missing.length > 0 && (
            <div className="pf-today__missing" data-testid="missing">
              <span className="pf-today__missing-label">{t("teacher.home.missingTitle")}</span>
              {missing.map((name) => (
                <span key={name} className="pf-chip-static pf-chip-static--warn">
                  {name}
                </span>
              ))}
              <span className="pf-muted pf-today__missing-note">
                {t("teacher.home.missingNote")}
              </span>
            </div>
          )}
          <div className="pf-today__go">
            {roomOpen ? (
              <ButtonLink
                to={roomConsolePath(roomOpen)}
                variant="green"
                className="pf-today__btn"
                data-testid="open-room"
              >
                {t("teacher.home.roomConsole")}
              </ButtonLink>
            ) : todayCanOpen ? (
              <Button
                variant="accent"
                className="pf-today__btn"
                disabled={busy}
                data-testid="open-room"
                aria-label={t("teacher.home.openRoomOf", { title: today!.title })}
                onClick={() => void openFor(today)}
              >
                {t("teacher.home.openRoom")}
              </Button>
            ) : (
              // 오늘 열 활동이 없어도 코스 룸은 열 수 있다(활동 없이 — 예전 요약 칸의 '▶ 룸 열기')
              <Button
                variant="accent"
                className="pf-today__btn"
                disabled={busy}
                data-testid="open-room-now"
                onClick={() => void openFor()}
              >
                {t("teacher.home.openRoomNow")}
              </Button>
            )}
          </div>
        </section>
        <TeacherRoomCell room={room} />
      </div>

      {others.length > 0 && (
        <section className="pf-pane" aria-labelledby="act-title">
          <h2 id="act-title" className="pf-h3">
            {t("teacher.home.otherActivities")}
          </h2>
          <ul className="pf-acts" data-testid="activity-list">
            {others.map((a) => {
              const phase = activityPhase(a.status);
              return (
                <li key={a.id} className="pf-acts__row" data-activity={a.id} data-status={phase}>
                  <div className="pf-acts__main">
                    <b>{a.title}</b>
                    <span className="pf-muted">
                      {[modeOf(a), minutesOf(a), whenLabel(a, t, locale)]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </div>
                  <div className="pf-row pf-row--wrap pf-acts__chips">
                    <VisibilityChip a={a} t={t} to={`${pg}?tab=settings`} />
                    <StatusChip a={a} t={t} />
                    {phase === "done" && <AchLeftChip a={a} t={t} />}
                    {a.videoReady && (
                      // P3-S3 '영상 준비됨'(SCR v0.7 검토 4-4) — 콘솔 영상 탭으로(룸이 닫혀도 열린다)
                      <Link
                        to={videoPath(a.roomId)}
                        className="pf-chip-static pf-chip-static--ok pf-video-chip"
                        aria-label={t("teacher.home.videoReadyOf", { title: a.title })}
                        data-testid="activity-video"
                      >
                        {t("teacher.home.videoReady")}
                      </Link>
                    )}
                  </div>
                  <div className="pf-row pf-acts__go">
                    {phase === "done" ? (
                      <>
                        <ButtonLink to="/replay/recent" variant="ghost" className="pf-btn--sm">
                          {t("teacher.home.replayRow")}
                        </ButtonLink>
                        <ButtonLink
                          to={`${pg}?tab=reactions`}
                          variant="ghost"
                          className="pf-btn--sm"
                        >
                          {t("teacher.home.reactionsRow")}
                        </ButtonLink>
                      </>
                    ) : (
                      <>
                        <ButtonLink
                          to={`${pg}?tab=settings`}
                          variant="ghost"
                          className="pf-btn--sm"
                        >
                          {t("teacher.home.settingsRow")}
                        </ButtonLink>
                        {phase === "scheduled" && (
                          <ButtonLink to={pg} variant="ghost" className="pf-btn--sm">
                            {t("teacher.home.submissionsRow")}
                          </ButtonLink>
                        )}
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="pf-pane pf-tools" aria-labelledby="tools-title" data-testid="tools">
        <h2 id="tools-title" className="pf-h3">
          {t("teacher.home.tools")}
        </h2>
        <div className="pf-row pf-row--wrap">
          <ButtonLink to={pg} variant="ghost" icon="⚙" className="pf-btn--sm">
            {t("teacher.home.playground")}
          </ButtonLink>
          <ButtonLink to="/replay/recent" variant="ghost" className="pf-btn--sm">
            {t("teacher.home.replay")}
          </ButtonLink>
          <ButtonLink
            to={exchangePath(roomOpen)}
            variant="ghost"
            icon="🤝"
            className="pf-btn--sm"
            data-testid="home-exchange"
          >
            {t("teacher.home.exchange")}
            {received > 0 && (
              <span
                className="pf-chip-static pf-chip-static--info pf-xchg-badge"
                data-testid="home-exchange-received"
              >
                {t("teacher.home.exchangeReceived", { n: received })}
              </span>
            )}
          </ButtonLink>
          <ButtonLink
            to={NETCHECK_PATH}
            variant="ghost"
            icon="📶"
            className="pf-btn--sm"
            data-testid="home-netcheck"
          >
            {t("teacher.home.netcheck")}
          </ButtonLink>
        </div>
      </section>
    </ScreenRoot>
  );
}

/** 예열 준비 행 + 오늘(또는 예정 시각 없는) 예정 활동은 바로 열 수 있다 — 예약 예열 전에 교사가 먼저 여는 경우 */
function canOpen(a: TeacherActivity): boolean {
  const phase = activityPhase(a.status);
  return phase === "ready" || (phase === "scheduled" && isTodayOrUnset(a.scheduledAt));
}

/** 오늘 수업 = 예열 준비 활동 → 오늘 열 수 있는 예정 활동 → 첫 활동 */
export function pickToday(list: readonly TeacherActivity[]): TeacherActivity | undefined {
  return list.find((a) => activityPhase(a.status) === "ready") ?? list.find(canOpen) ?? list[0];
}

/** 활동 상태 칩(와이어 FS-01-04 — 예열 준비 · 작품 n/전체 · 종료 · 성취 n/전체) */
function StatusChip({ a, t }: { a: TeacherActivity; t: T }) {
  const phase = activityPhase(a.status);
  const known = a.uploaded != null && a.total != null;
  return (
    <span
      className={`pf-chip-static pf-chip-static--${phase === "ready" ? "run" : phase === "scheduled" ? "info" : "ok"}`}
      data-testid="activity-status"
    >
      {phase === "ready"
        ? t("teacher.home.status.ready")
        : phase === "scheduled"
          ? known
            ? t("teacher.home.status.collecting", { n: a.uploaded!, total: a.total! })
            : t("teacher.home.status.scheduled")
          : known
            ? t("teacher.home.status.closed", { n: a.uploaded!, total: a.total! })
            : t("teacher.home.status.done")}
    </span>
  );
}

/** 공개 범위 칩(보완 2.4) — 보여 주기만 · 누르면 놀이터 설정 '공개 · 프리셋 · 별명표' */
function VisibilityChip({ a, t, to }: { a: TeacherActivity; t: T; to: string }) {
  if (!a.visibility) return null;
  return (
    <Link to={to} className="pf-chip-static pf-today__vis" data-testid="activity-visibility">
      {t("teacher.home.visibilityChip", { v: t(`teacher.visibility.${a.visibility}`) })}
    </Link>
  );
}

/** 성취 보내기 남음(보완 2.3) — 끝난 활동에 성취를 다 보내지 못했을 때만 · 누르면 그 활동의 메모 · 성취 */
function AchLeftChip({ a, t }: { a: TeacherActivity; t: T }) {
  if (a.uploaded == null || a.total == null || a.uploaded >= a.total) return null;
  const label = t("teacher.home.achLeft", { n: a.total - a.uploaded });
  return a.roomId ? (
    <Link
      to={assessPath(a.roomId)}
      className="pf-chip-static pf-chip-static--warn"
      data-testid="ach-left"
    >
      {label}
    </Link>
  ) : (
    <span className="pf-chip-static pf-chip-static--warn" data-testid="ach-left">
      {label}
    </span>
  );
}

/** 받은 대기 초대 수(FS-11-08) — 못 읽으면 0(바로 가기는 그대로) */
function useReceivedInvites(): number {
  const api = useExchangeApi();
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!api) return;
    let alive = true;
    api
      .invites()
      .then((x) => alive && setN(x.received.filter((i) => i.state === "pending").length))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [api]);
  return n;
}
